import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { Code2, Palette, Music4 } from "lucide-react";

const TEAM = [
  {
    initials: "MA",
    name: "Marco Alvarez",
    role: "Code & Physics",
    quote: "I tuned the flap gravity 412 times. Worth it.",
    color: "bg-volt/15 text-volt border-volt/30",
    icon: Code2,
    id: "marco",
  },
  {
    initials: "PN",
    name: "Priya Nair",
    role: "Art & Animation",
    quote: "Every stadium starts as a doodle on a napkin.",
    color: "bg-gold/15 text-gold border-gold/30",
    icon: Palette,
    id: "priya",
  },
  {
    initials: "JO",
    name: "Jules Okafor",
    role: "Sound & Community",
    quote: "The goal 'ping' is a real stadium whistle, pitched up.",
    color: "bg-mint/15 text-mint border-mint/30",
    icon: Music4,
    id: "jules",
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-36">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <Reveal>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-mint mb-4">
            About Us
          </p>
          <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl tracking-tight leading-none">
            TWO DEVS. <span className="text-volt">ONE BALL.</span>
            <br />
            <span className="text-outline">ZERO CHILL.</span>
          </h1>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <div className="rounded-3xl overflow-hidden border border-line aspect-[4/3]">
            <img
              src="https://images.unsplash.com/photo-1676270891810-7da36dd02c85?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Playing Flappy Goal on a phone"
              className="w-full h-full object-cover"
            />
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <h2 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight">
            How Flappy Goal happened
          </h2>
          <p className="mt-5 text-slate-300 leading-relaxed">
            It started during a World Cup final, of all places. Half-time was
            dragging, someone pulled out an old flappy game, and the joke wrote
            itself: what if the pipes were goal posts, and the bird had to
            thread the ball through the goal?
          </p>
          <p className="mt-4 text-slate-300 leading-relaxed">
            Eight months of late nights later, that joke is a real game. A
            hand-tuned physics engine, stadiums from five continents, and a
            ball that curls, dips and bounces like the real thing — all driven
            by a single thumb.
          </p>
          <p className="mt-4 text-slate-300 leading-relaxed">
            We're a tiny indie studio with one rule: if a feature doesn't make
            you say "one more go", it doesn't ship.
          </p>
        </Reveal>
      </section>

      <Marquee />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <Reveal>
          <h2 className="font-display text-5xl sm:text-6xl tracking-tight leading-none">
            THE <span className="text-gold">SQUAD</span>
          </h2>
        </Reveal>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {TEAM.map((t, i) => (
            <Reveal key={t.id} delay={i * 0.1}>
              <div
                data-testid={`team-card-${t.id}`}
                className="rounded-2xl border border-line bg-turf/70 p-8 h-full hover:border-volt/40 transition-colors duration-300"
              >
                <div className="flex items-center gap-4">
                  <span
                    className={`w-14 h-14 rounded-full border flex items-center justify-center font-head font-extrabold text-lg ${t.color}`}
                  >
                    {t.initials}
                  </span>
                  <div>
                    <h3 className="font-head text-xl font-bold">{t.name}</h3>
                    <p className="text-xs font-mono uppercase tracking-widest text-slate-400">
                      {t.role}
                    </p>
                  </div>
                </div>
                <p className="mt-5 text-sm text-slate-400 italic leading-relaxed">
                  "{t.quote}"
                </p>
                <t.icon size={18} className="mt-5 text-slate-500" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-28">
        <Reveal>
          <div className="rounded-3xl border border-line bg-turf/50 p-10 sm:p-14">
            <h2 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight">
              Under the hood
            </h2>
            <div className="mt-8 grid sm:grid-cols-3 gap-8">
              {[
                { k: "412", v: "gravity tunings before the flap felt right" },
                { k: "60fps", v: "locked frame rate, even on older phones" },
                { k: "5", v: "continents of stadiums at launch" },
              ].map((s, i) => (
                <div key={i} data-testid={`about-stat-${i}`}>
                  <p className="font-display text-5xl text-volt">{s.k}</p>
                  <p className="mt-2 text-sm text-slate-400">{s.v}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
